

import { useAuth } from "@/lib/auth/AuthContext";
import { IconLoader2, IconLogout } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useNavigate as useRouter } from "react-router-dom";
import { useState } from "react";

type LogoutButtonProps = {
  icon?: React.ElementType; // Icon before text
  size?: "big" | "medium" | "small" | "xsmall"; // Sizes
  color?: string; // Color
  textColor?: string; // Text color
  width?: string; // Width
  type?: "button" | "submit"; // Button type
  disabled?: boolean; // Disabled state
  margin?: string; // Margin
  iconSize?: number; // Icon size
  /**
   * Icon only: a 40×40 hit area with the `tryLogout` text as accessible name
   * and tooltip, `size`/`width` ignored. Additive — without it the markup is
   * exactly what every existing caller always got.
   */
  iconOnly?: boolean;
};
export const LogoutButton: React.FC<LogoutButtonProps> = ({
  size = "medium", // Default to medium size
  color = "cta-primary", // Default background color
  textColor = "font-light-primary", // Default text color
  width = "w-full",
  type = "button",
  disabled = false,
  margin = "",
  iconOnly = false,
}) => {
  const t = useTranslations("errors");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { logout } = useAuth();
  // Leave for /login whether or not the logout call got through: this button
  // sits on the outage strip, i.e. it is pressed exactly when the server may
  // not answer. Nothing awaits a click handler, so a re-thrown rejection
  // could only ever surface as "Uncaught (in promise)" noise.
  const logoutAndLeave = async () => {
    setLoading(true);
    try {
      await logout();
    } catch {
      // deliberate: the navigation below is the point
    } finally {
      router("/login");
    }
  };
  if (iconOnly) {
    const label = t("tryLogout");
    return (
      <button
        type={type}
        disabled={disabled}
        aria-label={label}
        title={label}
        className={`flex items-center justify-center h-10 w-10 rounded-lg ${color} ${textColor} ${
          disabled ? "opacity-50" : ""
        } ${margin} `}
        onClick={logoutAndLeave}
      >
        {loading ? (
          <IconLoader2 className="animate-spin" size={20} />
        ) : (
          <IconLogout size={20} />
        )}
      </button>
    );
  }
  const sizeStyles = {
    big: "px-6 py-3 body-1-medium rounded-lg",
    medium: "px-3 py-2 body-1-medium rounded-lg",
    small: "px-2 py-1 body-2-medium rounded-lg",
    xsmall: "px-2 py-1 body-3-medium rounded-md",
  };

  const buttonStyles = `
      ${sizeStyles[size]} 
      ${color}
      ${textColor}
    `;

  return (
    <button
      type={type}
      disabled={disabled}
      className={`flex flex-row justify-center items-center ${buttonStyles} ${width} ${
        disabled ? "opacity-50" : ""
      } ${margin} `}
      onClick={logoutAndLeave}
    >
      {loading ? (
        <IconLoader2 className="animate-spin mr-2" size={20} />
      ) : (
        <p>{t("tryLogout")}</p>
      )}
    </button>
  );
};
